// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Address } from '@medplum/fhirtypes';

export const US_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
] as const;

export type UsStateName = (typeof US_STATES)[number];

/** USPS two-letter codes for the 50 states + DC, keyed by the names in {@link US_STATES}. */
export const US_STATE_CODES: Readonly<Record<UsStateName, string>> = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  'District of Columbia': 'DC',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
};

/**
 * USPS codes outside the 50 states + DC that the US Core `us-core-usps-state` ValueSet accepts
 * (territories + military), so values written by the intake Questionnaire are never rejected.
 */
const US_TERRITORY_CODES: Readonly<Record<string, string>> = {
  'American Samoa': 'AS',
  Guam: 'GU',
  'Northern Mariana Islands': 'MP',
  'Puerto Rico': 'PR',
  'U.S. Virgin Islands': 'VI',
  'Virgin Islands': 'VI',
  'United States Minor Outlying Islands': 'UM',
  'Armed Forces Americas': 'AA',
  'Armed Forces Europe': 'AE',
  'Armed Forces Pacific': 'AP',
};

const CODE_BY_LOWERCASE_NAME: ReadonlyMap<string, string> = new Map(
  Object.entries({ ...US_STATE_CODES, ...US_TERRITORY_CODES }).map(([name, code]) => [name.toLowerCase(), code])
);

const VALID_CODES: ReadonlySet<string> = new Set(CODE_BY_LOWERCASE_NAME.values());

/** Select options that store the USPS code while showing the full state name. */
export const US_STATE_SELECT_OPTIONS: readonly { value: string; label: string }[] = US_STATES.map(
  (name: UsStateName) => ({ value: US_STATE_CODES[name], label: name })
);

/**
 * Resolves a free-form US state value ("Texas", "texas", "tx", " TX ") to its USPS two-letter code.
 * Returns undefined when the value is empty or not a recognized US state, territory, or military code.
 */
export function normalizeUsStateCode(state: string | undefined): string | undefined {
  const trimmed = state?.trim().replace(/\s+/g, ' ');
  if (!trimmed) {
    return undefined;
  }
  const upper = trimmed.toUpperCase();
  if (VALID_CODES.has(upper)) {
    return upper;
  }
  return CODE_BY_LOWERCASE_NAME.get(trimmed.toLowerCase());
}

export interface AddressStateNormalization {
  /** Addresses with every resolvable `state` rewritten to its USPS code. */
  addresses: Address[];
  /** True when at least one address's `state` was rewritten. */
  changed: boolean;
  /** Raw `state` values that could not be resolved to a USPS code. */
  unresolved: string[];
}

/**
 * Rewrites each address's `state` to its USPS two-letter code where it can be resolved.
 * Addresses without a `state` are left untouched; unrecognized values are left as-is and reported.
 */
export function normalizeAddressStates(addresses: Address[] | undefined): AddressStateNormalization {
  const unresolved: string[] = [];
  let changed = false;
  const normalized = (addresses ?? []).map((address: Address) => {
    if (!address.state?.trim()) {
      return address;
    }
    const code = normalizeUsStateCode(address.state);
    if (!code) {
      unresolved.push(address.state);
      return address;
    }
    if (code === address.state) {
      return address;
    }
    changed = true;
    return { ...address, state: code };
  });
  return { addresses: normalized, changed, unresolved };
}
